import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * HomePage — the logged-out gate. One button, plus the invitation hand-off.
 *
 * **Access is membership, not domain** (Omar, 2026-08-09). A person reaches a
 * customer's data because they were invited to that customer's Auth0 Organization
 * — one specific email address at a time, by that customer's own administrator.
 * What follows the `@` is irrelevant.
 *
 * So this screen asks nothing. It hands off to Auth0 with **no `organization`
 * parameter**; Auth0 prompts for credentials, works out which Organization the
 * person belongs to, and returns a token carrying it. That behaviour is the
 * application's `Login Flow = Prompt for Credentials` setting in the Auth0
 * dashboard, with `Types of Users = Business Users` ("users must be a member of an
 * organization to access the application"). Both are required; neither lives in
 * this repo, so changing them is a deploy step, not a code change.
 *
 * ## The one case where naming an Organization IS correct
 *
 * An Organization invitation email links back here carrying `?invitation=<ticket>`
 * and `?organization=org_…`. Auth0 requires the application to read those two
 * parameters off its own URL and forward them to `/authorize`; the ticket is what
 * actually creates the membership, and it was issued *for that specific
 * Organization*, so passing it back is not a domain gate returning by the back
 * door — it is the only way the invitation can be redeemed.
 *
 * Without this, the invitee lands on "Accedi", the ticket is silently discarded,
 * and the plain login that follows is refused because they are not yet a member of
 * anything. Since ACC-3 an invitation is the ONLY route to membership, so dropping
 * it closes the only door. Caught by an adversarial review on 2026-08-09; the
 * pre-ACC-3 screen accidentally covered this by naming the Organization itself.
 *
 * ## What this replaced, and why it had to go
 *
 * Until now this screen asked for a work email, resolved the *domain* to an
 * Organization via a public endpoint, and passed that Organization to Auth0 —
 * because the application was configured as `No Prompt`, which requires the app
 * to name the Organization itself.
 *
 * That design refused an unrecognised domain with no fallback, which was correct
 * *given* the design and fatal in practice: the domain index is globally unique, so
 * a shared consumer domain can never be claimed by one customer — and the first
 * real user signs in with an Apple `@me.com` address. **The only real person who
 * would ever have used it could not have logged in.** No test could see this; every
 * test invented an identity that satisfied whatever rule was being added. Found by
 * tracing an actual human through the screen. See the roadmap items `ACC-3`/`ACC-4`.
 */

type Invitation = { invitation: string; organization: string };

/** Auth0 appends both parameters to the invitation link; one without the other is
 * not a redeemable invitation, so we require the pair rather than guessing. */
function readInvitation(): Invitation | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const invitation = params.get("invitation");
  const organization = params.get("organization");
  return invitation && organization ? { invitation, organization } : null;
}

/** auth0-react surfaces OAuth failures as an error carrying the raw OAuth code.
 * `access_denied` is the one a human will actually hit, and the default message
 * ("Service not found", "Access denied") explains nothing to a non-technical user. */
function messageFor(error: Error): string {
  const code = (error as { error?: string }).error;
  if (code === "access_denied") {
    return (
      "Il tuo indirizzo non risulta collegato a nessuna organizzazione. " +
      "Apri il link dell'invito che hai ricevuto via email, oppure chiedi al tuo referente di invitarti."
    );
  }
  return "Accesso non riuscito. Riprova; se il problema persiste, contatta il tuo referente.";
}

function detailFor(error: Error): string {
  const description = (error as { error_description?: string }).error_description;
  return description ?? error.message;
}

export function HomePage(): JSX.Element {
  const { isLoading, loginWithRedirect, error } = useAuth0();
  const [busy, setBusy] = useState(false);
  // Synchronous latch: `busy` is React state and does not flip until the next
  // render, so two fast clicks both pass an `if (busy)` check and fire two
  // redirects. The ref flips in the same tick as the click.
  const claimed = useRef(false);

  // Read once per mount. Re-reading on every render would re-fire the effect
  // below with a fresh object identity and redirect in a loop.
  const invite = useMemo(readInvitation, []);

  const startLogin = useCallback(
    async function startLogin(): Promise<void> {
      if (claimed.current) return;
      claimed.current = true;
      setBusy(true);
      try {
        // The ONLY case where this app names an Organization: redeeming a ticket
        // Auth0 itself issued for that Organization. Otherwise no `organization`,
        // so Auth0 resolves it from the person's membership — passing one would
        // pin every user to a single customer, the bug ACC-3 removed.
        await loginWithRedirect(invite ? { authorizationParams: { ...invite } } : undefined);
      } catch {
        // The redirect failed, so the page is still here and the user needs to be
        // able to try again. Releasing the latch is the whole point of catching.
        claimed.current = false;
        setBusy(false);
      }
    },
    [invite, loginWithRedirect],
  );

  // Arriving from an invitation email is already an expression of intent — the
  // person clicked a link addressed to them — so redeem it without a second click.
  // Guarded by the same latch, so it fires once per page load. A refusal comes
  // back on the callback URL as `?error=`, which carries no `invitation`, so this
  // cannot loop.
  useEffect(() => {
    if (invite && !isLoading && !error) void startLogin();
  }, [invite, isLoading, error, startLogin]);

  // Back-navigation restores this page from the bfcache with the latch still set
  // and the button still disabled, stranding the user on a dead control. The
  // screen this replaced released it; only releasing on `catch` regressed that.
  useEffect(() => {
    function release(event: PageTransitionEvent): void {
      if (event.persisted) {
        claimed.current = false;
        setBusy(false);
      }
    }
    window.addEventListener("pageshow", release);
    return () => window.removeEventListener("pageshow", release);
  }, []);

  // An error means the redirect came back and failed, so the latch must not stay
  // set — otherwise "Riprova" is a dead button.
  useEffect(() => {
    if (error) {
      claimed.current = false;
      setBusy(false);
    }
  }, [error]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-ink">Caricamento…</p>
      </div>
    );
  }

  return (
    <main className="mx-auto flex h-full max-w-3xl flex-col justify-center px-6 py-12">
      <h1 className="font-mono text-3xl font-semibold text-brand">CME Studio</h1>
      <p className="mt-2 text-ink/80">
        Analisi gare d&apos;appalto · Computo Metrico Estimativo · Knowledge Base multi-tenant.
      </p>

      <div className="mt-8 rounded-lg border border-line bg-white p-6 shadow-sm">
        {error ? (
          <div role="alert" className="mb-4 rounded border border-red-300 bg-red-50 p-3">
            <p className="text-sm text-red-800">{messageFor(error)}</p>
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-red-700">Dettagli tecnici</summary>
              <p className="mt-1 break-words font-mono text-xs text-red-700">{detailFor(error)}</p>
            </details>
          </div>
        ) : null}

        <p className="text-sm text-ink/70">
          {invite
            ? "Stiamo aprendo il tuo invito…"
            : "Accedi con l'indirizzo email a cui hai ricevuto l'invito."}
        </p>
        <button
          type="button"
          disabled={busy}
          onClick={() => void startLogin()}
          className="mt-4 rounded bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand/90 disabled:opacity-60"
        >
          {busy ? "Accesso…" : error ? "Riprova" : "Accedi"}
        </button>
      </div>
    </main>
  );
}
